/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, OnInit, DoCheck, ViewChild, AfterViewInit} from '@angular/core';
import {WidgetService} from '../../services/widget.service';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {WidgetConfigLookup} from '../../../common/models/widget-config.model';
import {LookupService} from '../../../common/service/lookup.service';
import {NlrSanitizeResourcePipe} from '../../../common/pipe/nlr-sanitize-resource-pipe';
import {MapService} from '../../services/map.service';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {MapToolsService} from '../../services/map-tools.service';
import {NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {IDENTIFY_FEATURES_ORDERING} from '../../../common/constants';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {IdentifyFeaturesService} from './identify-features.service';
import {HelpLinkComponent} from '../../../help/help-link.component';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';
import {tap} from 'rxjs/operators';

@Component({
    selector: 'nlr-identify-features',
    imports: [CommonModule, FormsModule, NlrSanitizeResourcePipe, CdkDrag, CdkDragHandle, HelpLinkComponent],
    templateUrl: './identify-features.component.html',
    styleUrl: './identify-features.component.scss'
})
export class IdentifyFeaturesComponent implements OnInit, DoCheck, AfterViewInit {
    @ViewChild('widget')
    widgetElement: HTMLElement;

    widgetConfig: WidgetConfigLookup = {};
    title = 'IdentifiedFeaturesController';
    widgetId = 'identifyFeatures';
    open = true;

    lineTypeLookup = undefined;
    pointTypeLookup = undefined;
    legalParcelTypeLookup = undefined;
    controlPointTypeLookup = undefined;
    constructPointTypeLookup = undefined;

    attrsList = [];
    selectedAttrsList = [];
    featureLayerMap = {};

    rows = undefined;
    selectedRows = undefined;

    ordering = IDENTIFY_FEATURES_ORDERING;

    lastAttrsListIdx = 0;

    widgetVisibleAtLastCheck: boolean;

    helpLinkUrl: string = HELP_LINK_CONTENTS_BASE_URL + 'identify-features-help.html';

    helpPageTitle: string = 'Identified Features Tool';

    constructor(
        private identifyFeaturesService: IdentifyFeaturesService,
        private lookupService: LookupService,
        private widgetService: WidgetService,
        private mapService: MapService,
        private spatialUtilService: SpatialUtilService,
        private mapToolsService: MapToolsService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit() {
        console.debug(this.title + ' controller: initiated');
        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find((l: WidgetConfigLookup) => l.name === this.widgetId);
        this.rows = this.attrsList;
        this.selectedRows = this.selectedAttrsList;

        this.eventBus
            .on('clearIdentifyFeatures')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.onClear();
            });

        this.eventBus
            .on('clearSelectedFeatures')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.onClear();
            });

        this.eventBus
            .on('featureHighlightsChanged')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                    console.debug('on featureHighlightChanges identify-features');
                })
            )
            .subscribe(() => this.getHighlightedFeatures());

        this.getHighlightedFeatures();
    }

    ngDoCheck() {
        if (this.widgetElement && !this.widgetVisibleAtLastCheck) {
            this.widgetService.setupWidget(this.widgetId);
        }
        this.widgetVisibleAtLastCheck = !!this.widgetElement;

        if (this.widgetElement) {
            this.attrsList
                .sort((a, b) => {
                    if (a.sorting !== b.sorting) {
                        return a.sorting - b.sorting;
                    }

                    if (a.description !== b.description) {
                        return a.description.localeCompare(b.description);
                    }

                    return a.id - b.id;
                })
                .map(attr => `${attr.featureType}:${attr.id}`)
                .join();
        }
    }

    ngAfterViewInit() {
        this.identifyFeaturesService.setPosition();
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        const dragPosition = this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
        this.identifyFeaturesService.updatePositionFromDragPosition(dragPosition);
        return dragPosition;
    };

    toggleWidgetContentVisibility() {
        this.widgetService.showOrHideWidgetContent('identifyFeatures', this.open);
        this.open = !this.open;
    }

    onClose() {
        this.mapToolsService.deactivate('IDENTIFY_FEATURES_WINDOW');
    }

    onClear() {
        Object.keys(this.featureLayerMap).forEach(featureLayerId => {
            const filteredGraphics = Object.keys(this.featureLayerMap[featureLayerId]).map(id => this.featureLayerMap[featureLayerId][id]);
            if (filteredGraphics.length) {
                this.spatialUtilService.unhighlightFeatures(featureLayerId, filteredGraphics);
            }
        });
        this.selectedAttrsList = [];
    }

    onRowClick(event: any, attrs: any) {
        const attrsListIdx = this.attrsList.indexOf(attrs);
        if (event.ctrlKey) {
            const selectedAttrsIdx = this.selectedAttrsList.indexOf(attrs);
            if (selectedAttrsIdx === -1) {
                this.selectedAttrsList.push(attrs);
                this.spatialUtilService.highlightFeatures(attrs.featureLayerId, [attrs.id]);
            } else {
                this.selectedAttrsList.splice(selectedAttrsIdx, 1);
                this.spatialUtilService.unhighlightFeatures(attrs.featureLayerId, [attrs.id]);
            }
        } else if (event.shiftKey) {
            const minAttrsListIdx = Math.min(attrsListIdx, this.lastAttrsListIdx);
            const maxAttrsListIdx = Math.max(attrsListIdx, this.lastAttrsListIdx);

            if (this.selectedAttrsList.includes(attrs)) {
                for (let i = minAttrsListIdx; i <= maxAttrsListIdx; i++) {
                    const attrsListItem = this.attrsList[i];
                    const selectedAttrsIdx = this.selectedAttrsList.indexOf(attrsListItem);
                    console.log(selectedAttrsIdx);
                    if (selectedAttrsIdx !== -1) {
                        this.selectedAttrsList.splice(selectedAttrsIdx, 1);
                        this.spatialUtilService.unhighlightFeatures(attrsListItem.featureLayerId, [attrsListItem.id]);
                    }
                }
            } else {
                for (let i = minAttrsListIdx; i <= maxAttrsListIdx; i++) {
                    const attrsListItem = this.attrsList[i];
                    this.selectedAttrsList.push(attrsListItem);
                    this.spatialUtilService.highlightFeatures(attrsListItem.featureLayerId, [attrsListItem.id]);
                }
            }
        } else {
            this.spatialUtilService.unhighlightAllFeatures();
            this.selectedAttrsList = [];
            this.spatialUtilService.highlightFeatures(attrs.featureLayerId, [attrs.id]);
        }

        this.lastAttrsListIdx = attrsListIdx;
    }

    isSelected(attrs: any) {
        return this.selectedAttrsList.includes(attrs);
    }

    getHighlightedFeatures() {
        const selectionSets = this.spatialUtilService.getHighlightedFeatureSets(this.mapService.getGeometryFeatureLayers());

        selectionSets.forEach(set => {
            const featureLayerId = set.featureLayer.id;

            this.featureLayerMap[featureLayerId] = this.featureLayerMap[featureLayerId] || {};

            const graphicAttrsList = set.graphics.map(g => {
                const graphicAttrs = structuredClone(g.attributes);
                const id = graphicAttrs.id;
                this.featureLayerMap[featureLayerId][id] = g;
                this.setDescription(graphicAttrs, featureLayerId);

                const idx = this.attrsList.findIndex(attrs => attrs.featureLayerId === featureLayerId && attrs.id === id);

                if (idx === -1) {
                    this.attrsList.push(graphicAttrs);
                    this.selectedAttrsList.push(graphicAttrs);
                } else {
                    this.attrsList.splice(idx, 1, graphicAttrs);
                    const selectedAttrsIdx = this.selectedAttrsList.findIndex(
                        attrs => attrs.featureLayerId === graphicAttrs.featureLayerId && attrs.id === graphicAttrs.id
                    );
                    if (selectedAttrsIdx !== -1) {
                        this.selectedAttrsList.splice(selectedAttrsIdx, 1, graphicAttrs);
                    } else {
                        this.selectedAttrsList.push(graphicAttrs);
                    }
                }

                return graphicAttrs;
            });

            this.selectedAttrsList.splice(
                0,
                this.selectedAttrsList.length,
                ...this.selectedAttrsList.filter(attrs => attrs.featureLayerId !== featureLayerId || graphicAttrsList.includes(attrs))
            );
        });

        const allFeaturesMap = this.mapService.getGeometryFeaturesMap();
        const pointFeatureClasses = ['point', 'controlpoint'];

        Object.keys(allFeaturesMap).forEach(featureLayerId => {
            const featuresToUnhighlight = this.attrsList
                .filter(attrs => attrs.featureLayerId === featureLayerId && !allFeaturesMap[featureLayerId][attrs.id])
                .map(attrs => ({attributes: attrs}));

            this.attrsList.splice(0, this.attrsList.length, ...this.attrsList.filter(attrs => allFeaturesMap[attrs.featureLayerId][attrs.id]));

            this.selectedAttrsList.splice(
                0,
                this.selectedAttrsList.length,
                ...this.attrsList.filter(attrs => this.selectedAttrsList.includes(attrs))
            );

            const allFeaturesToUnhighlight = [
                ...featuresToUnhighlight,
                ...this.spatialUtilService.getMapGraphicsArray().filter(
                    g =>
                        g.attributes &&
                        (!g.attributes.visible ||
                            Object.keys(allFeaturesMap).some(k => {
                                const featureClass = this.spatialUtilService.getFeatureClass(k);
                                const featureClassesToCheck = pointFeatureClasses.includes(featureClass) ? pointFeatureClasses : [featureClass];
                                const keysToCheck = pointFeatureClasses.includes(featureClass)
                                    ? ['pointFeatureLayer', 'controlPointFeatureLayer']
                                    : [k];

                                const attributeFeatureClass = g.attributes ? g.attributes['featureClass'] : undefined;
                                const attributeId = g.attributes ? g.attributes['id'] : undefined;
                                return (
                                    featureClassesToCheck.includes(attributeFeatureClass) &&
                                    keysToCheck.every(keyToCheck => !allFeaturesMap[keyToCheck][attributeId])
                                );
                            }))
                )
            ];

            if (allFeaturesToUnhighlight.length) {
                this.spatialUtilService.unhighlightFeatures(featureLayerId, allFeaturesToUnhighlight);
            }
        });
    }

    setDescription(attrs: any, featureLayerId: any) {
        attrs.featureLayerId = featureLayerId;
        switch (featureLayerId) {
            case 'angleFeatureLayer':
                attrs.sorting = this.ordering[featureLayerId] ? this.ordering[featureLayerId] : Object.keys(this.ordering).length + 1;
                attrs.featureType = 'Angle';
                attrs.description = attrs.internalangle || '';
                break;
            case 'annotationFeatureLayer':
                {
                    attrs.sorting = this.ordering[featureLayerId] ? this.ordering[featureLayerId] : Object.keys(this.ordering).length + 1;
                    attrs.featureType = 'Annotation';
                    let tmpDesc = '';
                    if (attrs.featuretype === 'lot') {
                        for (let i = 1; i <= 3; i++) {
                            const att = 'attribute' + i;
                            if (Object.hasOwn(attrs, att)) {
                                if (attrs[att]) {
                                    if (i === 3) {
                                        tmpDesc += '-(' + attrs[att] + ')';
                                    } else if (tmpDesc) {
                                        tmpDesc = attrs[att];
                                    } else {
                                        tmpDesc = attrs[att];
                                    }
                                }
                            }
                        }
                    } else {
                        for (let i = 1; i <= 10; i++) {
                            const att = 'attribute' + i;
                            if (Object.hasOwn(attrs, att)) {
                                if (attrs[att]) {
                                    if (tmpDesc) {
                                        tmpDesc += '-' + attrs[att];
                                    } else {
                                        tmpDesc = attrs[att];
                                    }
                                }
                            }
                        }
                    }
                    attrs.description = '[' + attrs.featuretype.charAt(0).toUpperCase() + attrs.featuretype.slice(1) + '] ' + tmpDesc;
                }
                break;
            case 'lineFeatureLayer':
                if (!this.lineTypeLookup) {
                    this.lineTypeLookup = this.lookupService.getLookups('lineType');
                }
                if (this.lineTypeLookup.find(l => l.id === attrs.type).description === 'Offset Line') {
                    attrs.sorting = this.ordering['constructLineFeatureLayer']
                        ? this.ordering[featureLayerId]
                        : Object.keys(this.ordering).length + 1;
                    attrs.featureType = 'Offset Line';
                    attrs.description = attrs.distance ? Number(Math.round(Number(attrs.distance + 'e' + 2)) + 'e-' + 2) + 'm' : '';
                } else {
                    attrs.sorting = this.ordering[featureLayerId] ? this.ordering[featureLayerId] : Object.keys(this.ordering).length + 1;
                    attrs.featureType = 'Line';
                    attrs.description =
                        (attrs.distance ? Number(Math.round(Number(attrs.distance + 'e' + 2)) + 'e-' + 2) + 'm' : '') +
                        (attrs.distance && attrs.type ? ' - ' : '') +
                        (attrs.type ? this.lineTypeLookup.find(l => l.id === attrs.type).description : '');
                }
                break;
            case 'pointFeatureLayer':
                if (!this.pointTypeLookup) {
                    this.pointTypeLookup = this.lookupService.getLookups('pointType');
                }
                attrs.sorting = this.ordering[featureLayerId] ? this.ordering[featureLayerId] : Object.keys(this.ordering).length + 1;
                attrs.featureType = 'Point';
                attrs.description = this.pointTypeLookup.find(l => l.id === attrs.type).description;
                break;
            case 'polygonFeatureLayer':
                attrs.sorting = this.ordering[featureLayerId] ? this.ordering[featureLayerId] : Object.keys(this.ordering).length + 1;
                if (!this.legalParcelTypeLookup) {
                    this.legalParcelTypeLookup = this.lookupService.getLookups('legalParcelType');
                }
                attrs.featureType = 'Polygon';
                attrs.description =
                    attrs.statedarea + 'm' + '2'.sup() + ' - ' + this.legalParcelTypeLookup.find(l => l.id === attrs.type).description;
                break;
            case 'controlPointFeatureLayer':
                if (!this.controlPointTypeLookup) {
                    this.controlPointTypeLookup = this.lookupService.getLookups('controlPointType');
                }
                attrs.sorting = this.ordering[featureLayerId] ? this.ordering[featureLayerId] : Object.keys(this.ordering).length + 1;
                attrs.featureType = 'Control Point';
                attrs.description = this.controlPointTypeLookup.find(l => l.id === attrs.type).description;
                break;
            case 'constructPointFeatureLayer':
                if (!this.pointTypeLookup) {
                    this.pointTypeLookup = this.lookupService.getLookups('pointType');
                }
                if (!this.constructPointTypeLookup) {
                    this.constructPointTypeLookup = this.lookupService.getLookups('constructType');
                }
                attrs.sorting = this.ordering[featureLayerId] ? this.ordering[featureLayerId] : Object.keys(this.ordering).length + 1;
                attrs.featureType = 'Offset Point';
                attrs.description =
                    this.pointTypeLookup.find(l => l.id === attrs.marktype).description +
                    '[' +
                    this.constructPointTypeLookup.find(l => l.id === attrs.constructtype).description +
                    ']';

                if (!this.constructPointTypeLookup) {
                    this.constructPointTypeLookup = this.lookupService.getLookups('constructType');
                }
                break;
            case 'constructLineFeatureLayer':
                attrs.sorting = this.ordering[featureLayerId] ? this.ordering[featureLayerId] : Object.keys(this.ordering).length + 1;
                attrs.featureType = 'Offset Line';
                attrs.description = Number(Math.round(Number(attrs.distance + 'e' + 2)) + 'e-' + 2) + 'm';
                break;
            default:
                console.debug(featureLayerId + ' : featureLayer not checked, ' + this.title);
        }
    }
}
